import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const EditSubmissionPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    caption: '',
    collaborators_text: ''
  });

  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [currentAsset, setCurrentAsset] = useState<any>(null);
  const [submission, setSubmission] = useState<any>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'confirmed' | 'error'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Check if caption and collaborators can be edited (only AFTER Step 1 approval)
  const canEditCaptionAndCollaborators = submission && 
    (submission.status === 'APPROVAL_1_APPROVED' || 
     submission.status === 'APPROVAL_2_PENDING' ||
     submission.status === 'APPROVAL_2_APPROVED' ||
     (submission.status === 'REJECTED' && submission.approvals && submission.approvals.some((a: any) => a.step === 1 && a.decision === 'APPROVED')));

  useEffect(() => {
    fetchSubmission();
  }, [id]);

  const fetchSubmission = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await axios.get(`${API_URL}/api/v1/submissions/${id}`);
      const submission = response.data;
      
      setSubmission(submission); // Store full submission for status checks
      setFormData({
        title: submission.title,
        description: submission.description,
        caption: submission.caption || '',
        collaborators_text: submission.collaborators_text || ''
      });
      setCurrentAsset(submission.assets && submission.assets[0]); // Use first asset from array
      if (showLoading) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch submission:', error);
      setError('Failed to load submission');
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);
    setUploadStatus('idle');

    // Create preview for images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);
    } else if (selectedFile.type.startsWith('video/')) {
      const videoUrl = URL.createObjectURL(selectedFile);
      setFilePreview(videoUrl);
    } else {
      setFilePreview(null);
    }
  };

  const updateSubmissionDetails = async () => {
    try {
      await axios.patch(`${API_URL}/api/v1/submissions/${id}`, {
        title: formData.title,
        description: formData.description,
        caption: formData.caption || null,
        collaborators_text: formData.collaborators_text || null
      });
    } catch (err: any) {
      throw new Error(err.response?.data?.detail || 'Failed to update submission');
    }
  };

  const uploadNewAsset = async () => {
    if (!file) return;

    setUploadStatus('uploading');
    setUploadProgress(10);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('files', file); // Changed from 'file' to 'files'
      
      await axios.post(
        `${API_URL}/api/v1/submissions/${id}/asset/upload`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const progress = 10 + Math.round((progressEvent.loaded / progressEvent.total) * 85);
              setUploadProgress(progress);
            }
          }
        }
      );
      
      setUploadProgress(100);
      setUploadStatus('confirmed');
      
      // Wait a moment for backend to process
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh submission data to show new asset (without showing loading spinner)
      await fetchSubmission(false);
      
      // Clear file selection and reset status after a short delay to show success message
      setTimeout(() => {
        setFile(null);
        setFilePreview(null);
        setUploadStatus('idle');
        setUploadProgress(0);
      }, 1500);
    } catch (err: any) {
      console.error('Upload error:', err);
      
      // Handle different error formats
      let errorMessage = 'Upload failed';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => 
            typeof e === 'string' ? e : (e.msg || JSON.stringify(e))
          ).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setUploadStatus('error');
      setUploadProgress(0);
      throw err;
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Please fill in title and description');
      return;
    }

    setError(null);

    try {
      // Update submission details
      await updateSubmissionDetails();

      // Upload new asset if selected
      if (file) {
        await uploadNewAsset();
      }

      alert('Submission updated successfully!');
      navigate(`/submissions/${id}`);
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.message || 'Failed to save changes');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-3 sm:px-4">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => navigate(`/submissions/${id}`)}
            className="text-blue-600 hover:text-blue-800 mb-3 sm:mb-4 inline-block"
          >
            ← Back to Submission
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Edit Submission</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Update your submission details</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter submission title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe your submission"
            />
          </div>

          {/* Caption - Only show after Step 1 approval */}
          {canEditCaptionAndCollaborators && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Caption (Optional)
              </label>
              <textarea
                name="caption"
                value={formData.caption}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter social media caption"
              />
            </div>
          )}

          {/* Collaborators - Only show after Step 1 approval */}
          {canEditCaptionAndCollaborators && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Collaborators (Optional)
              </label>
              <textarea
                name="collaborators_text"
                value={formData.collaborators_text}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter collaborators (one per line or as free text)"
              />
              <p className="text-xs text-gray-500 mt-1">Add collaborators for this submission</p>
            </div>
          )}

          {/* Current Asset */}
          {currentAsset && currentAsset.storage_key && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Asset
              </label>
              <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                <p className="text-sm font-medium text-gray-900">{currentAsset.original_filename}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {(currentAsset.size_bytes / 1024 / 1024).toFixed(2)} MB • {currentAsset.mime_type}
                </p>
                <p className="text-xs text-blue-600 mt-2">Upload a new file below to replace this asset</p>
              </div>
            </div>
          )}

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload New Asset (Optional)
            </label>
            
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {!file ? (
                <div>
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <p className="mt-2 text-sm text-gray-600">
                    Drag and drop your file here, or click to browse
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Supported: Images, Videos, Audio (MP3, WAV), PDF, ZIP files
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type}
                  </p>
                  {filePreview && file.type.startsWith('image/') && (
                    <img src={filePreview} alt="Preview" className="mt-4 max-h-48 mx-auto rounded" />
                  )}
                  {filePreview && file.type.startsWith('video/') && (
                    <video src={filePreview} className="mt-4 max-h-48 mx-auto rounded" controls />
                  )}
                </div>
              )}
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              className="hidden"
              accept="image/*,video/*,audio/*,application/pdf,application/zip"
            />
          </div>

          {/* Upload Progress */}
          {uploadStatus === 'uploading' && (
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Uploading new asset...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Success Message */}
          {uploadStatus === 'confirmed' && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-green-800 font-medium">✓ Asset uploaded successfully!</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleSave}
              className="flex-1 py-3 px-6 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors"
            >
              Save Changes
            </button>
            
            <button
              onClick={() => navigate(`/submissions/${id}`)}
              className="px-6 py-3 border border-gray-300 rounded-md font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditSubmissionPage;
